import { Lock } from 'lucide-react';
import { useT } from '../App';
import { useSubscription } from '../context/SubscriptionContext';
import { CHECKOUT } from '../constants/checkout';

const FEATURE_LABELS = {
  export:         'Export to PowerPoint',
  versionHistory: 'Version History',
  recycleBin:     'Recycle Bin',
  teamWorkspace:  'Team Workspaces',
  propose:        'AI Proposal Writing',
  eligibility:    'Eligibility Checker',
  driveSync:      'Drive Sync',
  excelExport:    'Excel Export',
  schemas:        'Donor Schemas',
};

const FEATURE_UPSELL = {
  propose: {
    headline: 'Write winning proposals in minutes',
    body: 'AI Proposal Writing drafts a full USAID or PEPFAR narrative in under 5 minutes — grounded in live funder research. At $49/month, that\'s less than one hour of a consultant\'s time.',
    cta: 'Unlock Proposal Writing',
  },
  eligibility: {
    headline: 'Know if you qualify before you spend 3 hours applying',
    body: 'The Eligibility Checker scores your organization against every requirement in the NOFO and flags gaps instantly. Avoid wasted proposals.',
    cta: 'Unlock Eligibility Checker',
  },
  teamWorkspace: {
    headline: 'Your whole team, one pipeline',
    body: 'Team Workspaces let every member of your grants team see the same live data — active grants, applications, deadlines. No more emailing spreadsheets.',
    cta: 'Unlock Team Workspaces — $149/month',
    plan: 'org',
  },
  export: {
    headline: 'One-click PowerPoint from any grant',
    body: 'Export a full grant briefing deck for your leadership team in seconds. Includes key dates, amounts, eligibility, and your AI analysis.',
    cta: 'Unlock PowerPoint Export',
  },
  excelExport: {
    headline: 'Export your full grant tracker to Excel',
    body: 'Download your entire pipeline as a formatted .xlsx file — for reporting, board meetings, or offline backup.',
    cta: 'Unlock Excel Export',
  },
  versionHistory: {
    headline: 'Never lose a grant record',
    body: 'Version History keeps 30 days of snapshots for every grant — see what changed, when, and by whom. Restore any version in one click.',
    cta: 'Unlock Version History',
  },
  driveSync: {
    headline: 'Keep your Grant Drive organized automatically',
    body: 'Drive Sync creates folders for each grant in Google Drive and keeps your documents organized by status — Active, Applications, Completed.',
    cta: 'Unlock Drive Sync',
  },
  schemas: {
    headline: 'Donor-specific extraction templates',
    body: 'Donor Schemas apply USAID, PEPFAR, EU, and Gates Foundation-specific field mappings so the AI extracts exactly what each funder\'s portal expects.',
    cta: 'Unlock Donor Schemas',
  },
};

// Single source of price truth — constants/checkout.js (matches live Flutterwave links).
// Upgrade CTAs open the in-app pricing modal (annual/monthly + seats logic lives there);
// the modal opens the Flutterwave checkout in a new tab.
const openPricing = () => window.dispatchEvent(new CustomEvent('nofodesk:open-pricing'));

const FEATURE_CHECK = {
  export:         s => s.canExport,
  versionHistory: s => s.canVersionHistory,
  recycleBin:     s => s.canRecycleBin,
  teamWorkspace:  s => s.canTeamWorkspace,
  propose:        s => s.canPropose,
  eligibility:    s => s.canEligibility,
  driveSync:      s => s.canDriveSync,
  excelExport:    s => s.canExcelExport,
  schemas:        s => s.canSchemas,
};

/* Wraps a block — shows upgrade prompt if user can't access the feature.
   Usage: <PlanGate feature="export"><ExportButton /></PlanGate>        */
export function PlanGate({ feature, children, inline = false, upsellHint }) {
  const sub     = useSubscription();
  const T       = useT();
  const allowed = FEATURE_CHECK[feature]?.(sub) ?? true;
  const upsell  = { ...(FEATURE_UPSELL[feature] || {}), ...(upsellHint || {}) };
  const isOrg   = upsell.plan === 'org';
  const price   = isOrg
    ? `$${CHECKOUT.team.monthly_price}/month`
    : `$${CHECKOUT.pro.monthly_price}/month`;

  if (sub.loading) return null;
  if (allowed)     return children;

  if (inline) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: T.textMuted, fontSize: 12 }}>
        <Lock size={11} /> Pro
      </span>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10,
      padding: '20px 24px', borderRadius: 10, border: `1px solid rgba(201,162,39,0.25)`,
      background: 'rgba(201,162,39,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Lock size={15} style={{ color: T.gold, flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
          {upsell.headline || `${FEATURE_LABELS[feature] || 'This feature'} requires Professional`}
        </span>
      </div>
      {upsell.body && (
        <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.6 }}>{upsell.body}</p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={openPricing}
          style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: T.gold, color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {upsell.cta || 'Upgrade to Pro'}
        </button>
        <span style={{ fontSize: 12, color: T.textMuted }}>{price} · cancel any time</span>
      </div>
    </div>
  );
}

/* Small inline button to upgrade — used inside gate prompts and elsewhere */
export function UpgradeButton({ T, label = 'View Plans', onClick }) {
  const handleClick = onClick || (() => {
    window.dispatchEvent(new CustomEvent('nofodesk:open-pricing'));
  });
  return (
    <button
      onClick={handleClick}
      style={{
        marginTop: 4, padding: '6px 16px', borderRadius: 6, border: 'none',
        background: T.gold, color: '#000', fontSize: 12, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

/* Disabled-style wrapper for buttons — shows lock icon + tooltip, still renders the button slot */
export function GatedButton({ feature, children, T }) {
  const sub     = useSubscription();
  const theme   = useT();
  const t       = T || theme;
  const allowed = FEATURE_CHECK[feature]?.(sub) ?? true;

  if (sub.loading) return null;
  if (allowed)     return children;

  const upsell = FEATURE_UPSELL[feature] || {};
  const isOrg  = upsell.plan === 'org';

  return (
    <div
      title={`Upgrade to ${isOrg ? 'Team' : 'Pro'} to unlock ${FEATURE_LABELS[feature] || 'this feature'}`}
      onClick={openPricing}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', opacity: 0.6 }}
    >
      <Lock size={13} style={{ color: t.gold }} />
      <span style={{ pointerEvents: 'none' }}>{children}</span>
    </div>
  );
}
